import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { Button, Grid } from '@material-ui/core';
import { Visibility } from '@material-ui/icons';
import {
  PageTemplateDashboard,
  PageTemplateHeader,
  PageTemplateHeaderActions
} from '@sierralabs/react-material-ui';

import { FetchError } from '../../common/api/api';
import HoveredCard from '../../common/card/HoveredCard';
import { formatDate } from '../../common/formatter';
import HeaderBackButton, {
  StyledPageTemplate
} from '../../common/HeaderBackButton';
import HeaderTitle from '../../common/HeaderTitle';
import PageTemplateAlert from '../../common/page-template/PageTemplateAlert';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import { useJiraConnectAPI } from '../jira-connect/JiraConnect.hooks';
import { useJiraServerContext } from '../jira-server/jira-server.context';
import TaskJiraIssueAssignCard from '../tasks/cards/TaskJiraIssueAssignCard';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import TaskStatusVersionCard from '../tasks/cards/TaskStatusVersionCard';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import HazardCauseInfoCard from './cards/HazardCauseInfoCard';
import HazardCauseInitialAssessmentCard from './cards/HazardCauseInitialAssessmentCard';
import HazardCauseResidualAssessmentCard from './cards/HazardCauseResidualAssesmentCard';
import HazardReferenceCard from './cards/HazardReferenceCard';
import {
  useFindHazardCauseById,
  useFindHazardCauseVersion,
  useHazardEntityPrefixId
} from './Hazard.hooks';
import {
  BaseHazardCausesCardProps,
  HazardBoardColumnEntity,
  HazardCause
} from './Hazard.interface';
import environment from '../../environments';
import { useUserContext } from '../../user/User.context';
import DeleteEntityCard from '../../common/cards/DeleteEntityCard';

const HazardCauseDetailPage: React.FC = () => {
  const {
    hazardId: paramHazardId = '',
    productId: paramProductId = '',
    causeId: paramCauseId = ''
  } = useParams<{ hazardId: string; productId: string; causeId: string }>();
  const hazardId: number = parseInt(paramHazardId) || 0;
  const productId: number = parseInt(paramProductId) || 0;
  const causeId: number = parseInt(paramCauseId) || 0;
  const [from = 'detail'] = useQueryParam('from', StringParam);
  const [version] = useQueryParam('ver', withDefault(NumberParam, undefined));
  const [hazardCause, setHazardCause] = useState<HazardCause | undefined>();
  const [, setDidUpdate] = useState<boolean>(false);
  const [, setError] = useState<FetchError | undefined>();
  const {
    hazardCause: data,
    isLoadingHazardCauses,
    findHazardCauseError,
    findHazardCause
  } = useFindHazardCauseById(hazardId, causeId);
  const {
    hazardCauseVersion: loadedHazardCauseVersion,
    findHazardCauseVersion
  } = useFindHazardCauseVersion(hazardId, causeId, version);
  const [hazardCauseVersion, setHazardCauseVersion] = useState(
    loadedHazardCauseVersion
  );

  const title: string = useHazardEntityPrefixId(
    hazardCause,
    HazardBoardColumnEntity.Cause
  );
  const cardProps: BaseHazardCausesCardProps = {
    hazardId,
    hazardCause: hazardCause!,
    hazardCauseVersion,
    onChange: (newHazardCause: Partial<HazardCause>) => {
      if (hazardCause) {
        // notify task components to refresh
        hazardCause.task = { ...hazardCause.task };
        setHazardCause({
          ...hazardCause,
          ...newHazardCause
        });
      }
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!hazardCause?.task) {
      return;
    }
    Object.assign(hazardCause.task, newTask);
    cardProps.onChange?.({});
    cardProps.onUpdate?.();
  };

  useEffect(() => {
    findHazardCause();
  }, [findHazardCause, hazardId, causeId]);
  useEffect(() => {
    if (version) {
      findHazardCauseVersion();
    } else {
      setHazardCauseVersion(undefined);
    }
  }, [findHazardCauseVersion, hazardId, causeId, version]);
  useEffect(
    () =>
      loadedHazardCauseVersion &&
      setHazardCauseVersion(loadedHazardCauseVersion),
    [loadedHazardCauseVersion]
  );

  useEffect(() => {
    data && setHazardCause(data);
  }, [data]);

  useEffect(() => {
    setError(findHazardCauseError);
  }, [findHazardCauseError]);
  const { hasRole } = useUserContext();
  const { isAvailable: isJiraConnect } = useJiraConnectAPI();
  const { isJiraServer } = useJiraServerContext();
  const { isFieldDisabled } = useTaskEntityFields(hazardCause?.task);
  const task = hazardCauseVersion?.task || hazardCause?.task;
  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        <HeaderBackButton
          to={
            from !== 'hazardTrace'
              ? `/portal/products/${productId}/hazards/${hazardId}/${from}`
              : `/portal/products/${productId}/${from}`
          }
        />
        <HeaderTitle>
          <h2>{isLoadingHazardCauses ? 'Loading...' : title}</h2>
          {hazardCause && (
            <div className='status-container'>
              <TaskStatusLabel task={hazardCause.task} />
            </div>
          )}
        </HeaderTitle>
        <PageTemplateHeaderActions flex>
          {hazardCause?.task && (
            <TaskStatusActions
              task={hazardCause.task}
              taskEntity={hazardCause}
              onChange={(task: Task) => {
                hazardCause.task = task;
                setHazardCause({ ...hazardCause });
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      {hazardCause && version && (
        <PageTemplateAlert severity='warning'>
          Viewing previous version {version}
          {hazardCauseVersion &&
            ` from ${formatDate(hazardCauseVersion.created)}. `}
          <Link to={`./${hazardCause.id}`}>
            Click here to view latest version
          </Link>
        </PageTemplateAlert>
      )}
      <PageTemplateDashboard>
        {hazardCause && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <HazardCauseInfoCard {...cardProps} />
                </Grid>
                {(isJiraConnect || isJiraServer) && (
                  <Grid item xs={12}>
                    <TaskJiraIssueAssignCard
                      task={hazardCause.task}
                      entity={{
                        title: hazardCause.title,
                        description: hazardCause.description
                      }}
                      jiraFieldColumns={
                        environment.hazardConfig?.cause?.visibleJiraFields
                      }
                      onChange={taskJiraIssues => {
                        // update hazardCause Task jira issues on change
                        hazardCause.task.jiraIssues = taskJiraIssues;
                        setHazardCause({ ...hazardCause });
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <HazardCauseInitialAssessmentCard
                    hazardId={hazardId}
                    cause={hazardCause}
                    onChange={cardProps.onChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <HazardCauseResidualAssessmentCard
                    hazardId={hazardId}
                    cause={hazardCause}
                    onChange={cardProps.onChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TaskAttachmentCard
                    task={hazardCause.task}
                    disabled={Boolean(hazardCauseVersion)}
                    attachments={hazardCauseVersion?.attachments}
                    onError={cardProps.onError}
                    onChange={handleTaskUpdate}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TaskStatusVersionCard
                    taskEntity={hazardCause}
                    renderContent={group =>
                      group.version !== hazardCause.task.version && (
                        <HoveredCard
                          title={`Version ${group.version}`}
                          content='View the previous version'
                          actions={
                            <Button
                              variant='text'
                              color='primary'
                              component={Link}
                              startIcon={<Visibility />}
                              to={`./${hazardCause.id}?ver=${group.version}`}
                            >
                              View
                            </Button>
                          }
                        />
                      )
                    }
                  />
                </Grid>
                {task?.children && task.children.length > 0 && (
                  <Grid item xs={12}>
                    <SubTaskListCard
                      title='Stakeholders'
                      task={task}
                      disabled={
                        Boolean(version) || isFieldDisabled('assignSubTasks')
                      }
                      onError={cardProps.onError}
                      onChange={handleTaskUpdate}
                    />
                  </Grid>
                )}
                {task && (
                  <Grid item xs={12}>
                    <TaskAssignCard task={task} />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TaskFileListCard
                    task={hazardCause.task}
                    disabled={Boolean(hazardCauseVersion)}
                    onError={cardProps.onError}
                    onChange={handleTaskUpdate}
                  />
                </Grid>
                <Grid item xs={12}>
                  <HazardReferenceCard
                    hazardId={hazardId}
                    productId={productId}
                    sourceEntityId={causeId}
                    version={version}
                    source='causes'
                    target='controls'
                    disabled={isFieldDisabled('assignControls')}
                  />
                </Grid>
                {hasRole('Delete') && (
                  <Grid item xs={12}>
                    <DeleteEntityCard
                      title='Hazard Cause'
                      apiPath={`hazards/${hazardId}/causes`}
                      entity={hazardCause}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>
    </StyledPageTemplate>
  );
};

export default HazardCauseDetailPage;
